/**
 * Shared bits for the COLLECTION scenes (Team/Boxes + Dex) — a soft type-pill,
 * a sprite-fitting helper, and a calm placeholder backdrop. Kept tiny and
 * presentational; these scenes read all truth from the GameSession.
 */
import Phaser from 'phaser';
import type { PokemonType } from '@/shared';
import { COLORS, FONT, RADIUS, SPACING } from '../ui/theme.js';
import { makeClickable } from '../ui/clickable.js';
import type { SpriteResolver } from '../sprites/index.js';
import type { Layout } from '../layout/index.js';

/** Soft, low-contrast colors per type — readable on the dusk surfaces, never harsh. */
export const TYPE_COLORS: Record<PokemonType, number> = {
  normal: 0x9aa0aa,
  fire: 0xe8896a,
  water: 0x6aa6e8,
  electric: 0xe8cf6a,
  grass: 0x84c98a,
  ice: 0x95d6d6,
  fighting: 0xcf7c72,
  poison: 0xb58ad0,
  ground: 0xd3b27a,
  flying: 0xa6b8e8,
  psychic: 0xe891b0,
  bug: 0xafc06a,
  rock: 0xc2b48a,
  ghost: 0x9389c4,
  dragon: 0x8a7fe0,
  dark: 0x8a8597,
  steel: 0xa9b4c4,
  fairy: 0xe8a8cf,
};

/** A small rounded type tag. Returns a container centered on (x, y). */
export function typePill(
  scene: Phaser.Scene,
  x: number,
  y: number,
  type: PokemonType,
): Phaser.GameObjects.Container {
  const label = scene.add
    .text(0, 0, type.toUpperCase(), {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.xs}px`,
      color: '#1a2233',
      fontStyle: 'bold',
    })
    .setOrigin(0.5);
  // Size the pill to the wrapped label, rounding up so sub-pixel text widths never
  // clip. The corner radius MUST be clamped to half the smaller side — Phaser's
  // fillRoundedRect produces severe artifacts (stray fill bleeding across the card)
  // when the radius exceeds the box, which RADIUS.pill (999) always would here.
  const w = Math.ceil(label.width) + SPACING.md;
  const h = FONT.size.xs + SPACING.sm;
  const r = Math.min(RADIUS.pill, w / 2, h / 2);
  const gfx = scene.add.graphics();
  gfx.fillStyle(TYPE_COLORS[type], 1);
  gfx.fillRoundedRect(-w / 2, -h / 2, w, h, r);
  const c = scene.add.container(x, y, [gfx, label]);
  c.setSize(w, h);
  return c;
}

/**
 * Make a collection TILE/CARD clickable the cozy-UI way: a CENTERED square (or
 * rectangle) hit area that exactly matches the drawn panel (dex tiles + box rows
 * draw their panel spanning [-size/2..size/2]), plus the pointer hand cursor on
 * hover. One call for the Dex tile, Box row, and Team slot so the hit area never
 * drifts from the visual and the mouse always reads as "clickable". Wraps the
 * shared ui/clickable helper; pass the tile's drawn width/height (square -> equal).
 */
export function makeTileClickable<T extends Phaser.GameObjects.GameObject>(
  tile: T,
  width: number,
  height: number,
  onClick: (pointer: Phaser.Input.Pointer) => void,
): T {
  return makeClickable(tile, width, height, { onClick });
}

/** Scale an image to fit a max side without distortion (and cap upscaling). */
export function fitSprite(img: Phaser.GameObjects.Image, maxSide: number, maxScale = 3): void {
  const w = img.width || 1;
  const h = img.height || 1;
  const scale = Math.min(maxSide / w, maxSide / h, maxScale);
  img.setScale(scale);
}

/**
 * Resolve + show a species' front sprite into an existing Image, fitting it to
 * `maxSide`. PREFERS the 3D-model render's STATIC first frame (so the dex GRID stays
 * smooth — no hundreds of simultaneous animations), falling back to the Gen5 battle
 * front sprite. Guards null + scene-active after the await (a tired player must never
 * see a crash). When `silhouette`, the sprite is tinted dark (unseen dex).
 */
export async function showSpecies(
  scene: Phaser.Scene,
  resolver: SpriteResolver,
  dexNo: number,
  img: Phaser.GameObjects.Image,
  maxSide: number,
  silhouette = false,
): Promise<void> {
  // The render's first frame (static) — preferred for the grid; never animates here.
  const render = await resolver.renderBattle(dexNo, 'front').catch(() => null);
  if (!scene.scene.isActive()) return;
  if (render) {
    img.setTexture(render.textureKey, 0);
  } else {
    const r = await resolver.battle(dexNo, 'front');
    if (!r || !scene.scene.isActive()) return;
    img.setTexture(r.key);
  }
  fitSprite(img, maxSide);
  if (silhouette) img.setTint(0x10151f).setAlpha(0.55);
  else img.clearTint().setAlpha(1);
  img.setVisible(true);
}

/**
 * Resolve + show a species into a SPRITE, playing the 3D render's gentle looping idle
 * (the dex DETAIL view). Falls back to the Gen5 battle front sprite (static) when no
 * render exists. Only the selected/detail sprite animates — the grid uses the static
 * {@link showSpecies} — so the dex scroll stays smooth. Reduced-motion holds frame 0.
 */
export async function showSpeciesAnimated(
  scene: Phaser.Scene,
  resolver: SpriteResolver,
  dexNo: number,
  sprite: Phaser.GameObjects.Sprite,
  maxSide: number,
  silhouette = false,
  reducedMotion = false,
): Promise<void> {
  const render = await resolver.renderBattle(dexNo, 'front').catch(() => null);
  if (!scene.scene.isActive()) return;
  if (render) {
    sprite.stop();
    sprite.setTexture(render.textureKey, 0);
    fitSprite(sprite, maxSide);
    if (!reducedMotion) sprite.play({ key: render.animKey, startFrame: 0 });
  } else {
    const r = await resolver.battle(dexNo, 'front');
    if (!r || !scene.scene.isActive()) return;
    sprite.stop();
    sprite.setTexture(r.key);
    fitSprite(sprite, maxSide);
  }
  if (silhouette) sprite.setTint(0x10151f).setAlpha(0.55);
  else sprite.clearTint().setAlpha(1);
  sprite.setVisible(true);
}

/** A calm programmatic backdrop: soft vertical wash + a faint floor band. Swappable. */
export function calmBackdrop(scene: Phaser.Scene, layout: Layout): Phaser.GameObjects.Graphics {
  const g = scene.add.graphics();
  redrawBackdrop(g, layout);
  return g;
}

export function redrawBackdrop(g: Phaser.GameObjects.Graphics, layout: Layout): void {
  const { width, height } = layout;
  g.clear();
  g.fillStyle(COLORS.bg, 1);
  g.fillRect(0, 0, width, height);
  // A barely-there lighter band near the top for gentle depth.
  g.fillStyle(COLORS.surface, 0.4);
  g.fillRect(0, 0, width, Math.min(120, height * 0.18));
}
